import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { IproductRegistration } from "../../models/epcCustomer.model";
import stamp from "../../assets/signs/stamp-1.png";
Font.register({
  family: "Helvetica",
  fonts: [{ src: "https://fonts.gstatic.com/s/helvetica/v1/Helvetica.ttf" }],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  mainBorder: {
    border: "1px solid black",
  },
  row: {
    flexDirection: "row",
    borderBottom: "1px solid black",
    minHeight: 20,
    alignItems: "center",
  },
  fullWidthHeader: {
    width: "100%",
    textAlign: "center",
    fontWeight: "bold",
    padding: 4,
  },
  twoColumnContainer: {
    flexDirection: "row",
  },
  leftCol: {
    width: "50%",
    borderRight: "1px solid black",
  },
  rightCol: {
    width: "50%",
  },
  sectionHeader: {
    textAlign: "center",
    fontWeight: "bold",
    borderBottom: "1px solid black",
    padding: 4,
    backgroundColor: "#f0f0f0",
  },
  detailRow: {
    flexDirection: "row",
    borderBottom: "1px solid black",
    padding: 4,
  },
  detailLabel: {
    width: "45%",
    fontWeight: "bold",
    fontSize: 9,
  },
  detailValue: {
    width: "55%",
    fontSize: 9,
  },
  serialHeaderRow: {
    flexDirection: "row",
    borderBottom: "1px solid black",
    backgroundColor: "#f0f0f0",
    height: 20,
    alignItems: "center",
  },
  srNoHeader: {
    width: "20%",
    borderRight: "1px solid black",
    textAlign: "center",
    fontWeight: "bold",
    height: "100%",
    paddingTop: 4,
  },
  panelSerialHeader: {
    width: "80%",
    textAlign: "center",
    fontWeight: "bold",
    height: "100%",
    paddingTop: 4,
  },
  inverterSerialHeader: {
    width: "100%",
    textAlign: "center",
    fontWeight: "bold",
    height: "100%",
    paddingTop: 4,
  },
  serialRow: {
    flexDirection: "row",
    borderBottom: "1px solid black",
    height: 18,
    alignItems: "center",
  },
  srNoCell: {
    width: "20%",
    borderRight: "1px solid black",
    textAlign: "center",
    height: "100%",
    paddingTop: 3,
  },
  serialCell: {
    width: "80%",
    textAlign: "center",
    height: "100%",
    paddingTop: 3,
  },
  inverterSerialCell: {
    width: "100%",
    textAlign: "center",
    height: "100%",
    paddingTop: 3,
  },
  signatureImage: {
    width: 235,
    height: 140,
    objectFit: "contain",
  },
  stamp: {
    alignItems: "flex-end",
  },
});

interface InverterSerialNumbersPdfProps {
  productRegistration: IproductRegistration;
  totalSolarCapacity: string;
}

export const InverterSerialNumbersPdf: React.FC<
  InverterSerialNumbersPdfProps
> = ({ productRegistration, totalSolarCapacity }) => {
  const eaName = "FRELIT ENERGY PRIVATE LIMITED";
  const userName = productRegistration?.name?.toUpperCase() || "";

  const panelCodes = productRegistration?.panelBarcodes || [];
  const inverterCodes = productRegistration?.inverterBarcodes || [];

  const minRows = Math.max(panelCodes.length, inverterCodes.length, 10);
  const displayRows = Array.from({ length: minRows });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.mainBorder}>
          <View style={styles.row}>
            <Text style={styles.fullWidthHeader}>NAME OF EA :- {eaName}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.fullWidthHeader}>
              NAME OF USER :- {userName}
            </Text>
          </View>

          <View style={styles.twoColumnContainer}>
            <View style={styles.leftCol}>
              <Text style={styles.sectionHeader}>SOLAR MODULES DETAILS</Text>

              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>PANEL MAKE</Text>
                <Text style={styles.detailValue}>
                  : {productRegistration?.panelMake || "-"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>CAPACITY</Text>
                <Text style={styles.detailValue}>
                  : {productRegistration?.panelCapacity || "-"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>NO. OF MODULES</Text>
                <Text style={styles.detailValue}>
                  : {productRegistration?.numberOfModules || "-"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>TOTAL CAPACITY</Text>
                <Text style={styles.detailValue}>
                  : {totalSolarCapacity || "-"}
                </Text>
              </View>

              <View style={styles.serialHeaderRow}>
                <Text style={styles.srNoHeader}>SR. NO</Text>
                <Text style={styles.panelSerialHeader}>PANEL SERIAL NO.</Text>
              </View>

              {displayRows.map((_, i) => {
                const code = panelCodes[i];
                return (
                  <View style={styles.serialRow} key={`panel-${i}`}>
                    <Text style={styles.srNoCell}>{code ? i + 1 : ""}</Text>
                    <Text style={styles.serialCell}>{code || ""}</Text>
                  </View>
                );
              })}
            </View>

            <View style={styles.rightCol}>
              <Text style={styles.sectionHeader}>INVERTER DETAILS</Text>

              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>INVERTER MAKE</Text>
                <Text style={styles.detailValue}>
                  : {productRegistration?.inverterMake || "-"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>CAPACITY</Text>
                <Text style={styles.detailValue}>
                  : {productRegistration?.inverterCapacity || "-"}
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>NO. OF INVERTERS</Text>
                <Text style={styles.detailValue}>: 01</Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>TOTAL CAPACITY</Text>
                <Text style={styles.detailValue}>
                  : {productRegistration?.inverterCapacity || "-"}
                </Text>
              </View>

              <View style={styles.serialHeaderRow}>
                <Text style={styles.inverterSerialHeader}>
                  INVERTER SERIAL NO.
                </Text>
              </View>

              {displayRows.map((_, i) => {
                const code = inverterCodes[i];
                return (
                  <View style={styles.serialRow} key={`inv-${i}`}>
                    <Text style={styles.inverterSerialCell}>{code || ""}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        </View>
        <View style={styles.stamp}>
          <Image src={stamp} style={styles.signatureImage} />
        </View>
      </Page>
    </Document>
  );
};
